import { useQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { get } from '../../../redux/api';
import type { Match } from '../../../../shared/model-typings';

//  We only store a subset of Match, since there is a lot of data such as the full license text which we don't want to cache in memory.
export type RevisionFileMatch = Pick<Match, 'id' | 'path' | 'sources' >;

type FileMatchesForRevisionResponse = { matches: Match[]; cliLicenseScanWithMatchData: boolean };

//  Helper for generating the URL search params for the request.
const getURLSearchParams = (revisionLocator?: string, licenseId?: string): URLSearchParams => {
  //  URLSearchParams automatically encodes params for us
  const params = new URLSearchParams();
  if (revisionLocator) {
    params.append('revisionId', revisionLocator);
  }
  if (licenseId) {
    params.append('licenseId', licenseId);
  }
  return params;
};

//  We only care about a subset of the returned data, Pick it here.
const toRevisionFileMatch = ({ id, path, sources }: Match): RevisionFileMatch => ({
  id,
  path,
  sources,
});

/**
 * Returns the file matches for a given revision.
 */
async function getFileMatchesForRevision(revisionLocator?: string, licenseId?: string) {
  const response = await get<FileMatchesForRevisionResponse>(`/api/matches?${getURLSearchParams(revisionLocator, licenseId).toString()}`);
  if (!response.ok || !response.data) {
    throw new Error(`Error fetching file matches for revision ${revisionLocator} and license ${licenseId}`);
  }
  return {
    matches: response.data.matches.map(toRevisionFileMatch),
    cliLicenseScanWithMatchData: response.data.cliLicenseScanWithMatchData,
  };
}

const getQueryKey = (revisionLocator?: string, licenseId?: string): QueryKey =>
  ['matches', revisionLocator, licenseId];

/**
 * Fetches the file matches for a given revision.
 * revisionLocator could technically be undefined while things are still loading,
 * so we don't enable the query until it's defined.
 *
 * @param {string} revisionLocator - The locator of the revision to fetch. No need to call nuc before passing it in here.
 * @param {string} licenseId - The license ID to filter the matches by.
 */
export default function useFileMatchesForRevision(
  revisionLocator?: string,
  licenseId?: string,
) {
  return useQuery({
    queryKey: getQueryKey(revisionLocator, licenseId),
    queryFn: () => getFileMatchesForRevision(revisionLocator, licenseId),
    enabled: !!revisionLocator,
  });
}
